<template>
  <div class="user-dashboard">
    <pop-component v-if="showPopComponent"></pop-component>

    <div class="dashboard-shell">
      <UserAppChrome back-mode="history" @section="scrollToSection" />

      <main class="dashboard-main">
        <section id="dashboard-home" class="dashboard-hero" aria-labelledby="dashboard-title">
          <div class="hero-copy">
            <p class="section-kicker"><span></span> Area riservata</p>
            <h1 id="dashboard-title">
              <span>Benvenuto nel tuo</span>
              pannello utente.
            </h1>

            <div class="account-actions">
              <button type="button" class="account-button account-button--primary" @click="toggleChangePasswordFields">
                <span class="material-icons" aria-hidden="true">lock_reset</span>
                <span class="account-button__label">Cambia password</span>
              </button>
              <button type="button" class="account-button" @click="goToComeFunziona">
                <span class="material-icons" aria-hidden="true">info_outline</span>
                <span class="account-button__label">Come funziona</span>
              </button>
            </div>
          </div>

          <div class="hero-visual">
            <img
              :src="heroDashboardImage"
              alt="Coaching online con il team Fusco Fit"
              width="1920"
              height="800"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
            <div class="hero-visual__shade"></div>
          </div>
        </section>

        <section v-if="showChangePasswordFields" class="password-panel" aria-label="Cambio password">
          <div class="password-panel__heading">
            <span class="material-icons" aria-hidden="true">lock</span>
            <div>
              <span class="password-panel__eyebrow">Account</span>
              <h2>Aggiorna la password</h2>
            </div>
          </div>
          <div class="password-panel__form">
            <input type="password" v-model="newPassword" placeholder="Nuova password" />
            <input type="password" v-model="confirmPassword" placeholder="Conferma nuova password" />
            <button type="button" class="confirm-button" @click="changePassword">
              Conferma
              <span class="material-icons" aria-hidden="true">arrow_forward</span>
            </button>
          </div>
          <div v-if="passwordChangeError" class="password-error">
            {{ passwordChangeError }}
          </div>
        </section>

        <DashboardVideoCard />

        <section id="dashboard-support" class="dashboard-section dashboard-section--support" aria-labelledby="support-title">
          <div class="section-heading">
            <p class="section-kicker"><span></span> Il team al tuo fianco</p>
            <div class="section-heading__copy">
              <h2 id="support-title">Assistenza</h2>
              <p class="section-heading__description">Scegli il supporto di cui hai bisogno: il nostro team è qui per te.</p>
            </div>
          </div>

          <div class="support-grid">
            <article class="support-card support-card--technical" @click="goToTicketsTecnico">
              <div class="feature-icon">
                <span class="material-icons" aria-hidden="true">fitness_center</span>
              </div>
              <div class="feature-copy">
                <h3>Assistenza tecnica</h3>
                <p>Scrivi nella conversazione diretta con il nostro team tecnico.</p>
              </div>
              <span class="feature-arrow material-icons" aria-hidden="true">north_east</span>
            </article>

            <article class="support-card support-card--nutrition" @click="goToTickets">
              <div class="feature-icon">
                <span class="material-icons" aria-hidden="true">set_meal</span>
              </div>
              <div class="feature-copy">
                <h3>Assistenza nutrizionale</h3>
                <p>Crea dei nuovi ticket diretti con il nostro team nutrizionale.</p>
              </div>
              <span class="feature-arrow material-icons" aria-hidden="true">north_east</span>
            </article>

          </div>
        </section>

        <section class="dashboard-section dashboard-section--premium" aria-labelledby="premium-title">
          <div class="premium-cluster" :class="{ 'premium-cluster--locked': !isTopProfile }">
            <div class="premium-cluster__heading">
              <div>
                <h2 id="premium-title">Servizi TOP</h2>
                <p v-if="isTopProfile">Assistenza dedicata e Coaching Day attivi per il tuo profilo.</p>
                <p v-else>Scopri i servizi esclusivi inclusi nei percorsi TOP.</p>
              </div>
              <span class="premium-cluster__badge" :class="{ 'premium-cluster__badge--locked': !isTopProfile }">
                <span v-if="!isTopProfile" class="material-icons" aria-hidden="true">lock</span>
                TOP
              </span>
            </div>

            <div class="premium-grid">
              <button
                type="button"
                class="premium-card premium-card--support"
                :class="{ 'premium-card--locked': !isTopProfile }"
                :aria-label="isTopProfile ? 'Apri assistenza per profili TOP' : 'Assistenza per profili TOP, servizio bloccato'"
                :aria-disabled="!isTopProfile ? 'true' : undefined"
                @click="handlePremiumService('support')"
              >
                <div class="premium-card__icon">
                  <span class="material-icons" aria-hidden="true">{{ isTopProfile ? 'support_agent' : 'lock' }}</span>
                </div>
                <div class="premium-card__copy">
                  <span>Servizio premium</span>
                  <h3>Assistenza per profili TOP</h3>
                </div>
                <span class="premium-card__arrow material-icons" aria-hidden="true">{{ isTopProfile ? 'north_east' : 'lock' }}</span>
              </button>

              <a
                class="premium-card premium-card--coaching"
                :class="{ 'premium-card--locked': !isTopProfile }"
                :href="isTopProfile ? coachingDayUrl : undefined"
                :role="!isTopProfile ? 'button' : undefined"
                :aria-label="isTopProfile ? 'Prenota il tuo appuntamento per il Coaching Day' : 'Coaching Day, servizio bloccato'"
                :aria-disabled="!isTopProfile ? 'true' : undefined"
                @click="handlePremiumService('coaching', $event)"
              >
                <div class="premium-card__icon">
                  <span class="material-icons" aria-hidden="true">{{ isTopProfile ? 'calendar_month' : 'lock' }}</span>
                </div>
                <div class="premium-card__copy">
                  <span>Coaching Day</span>
                  <h3>Prenota il tuo appuntamento</h3>
                </div>
                <span class="premium-card__arrow material-icons" aria-hidden="true">{{ isTopProfile ? 'north_east' : 'lock' }}</span>
              </a>
            </div>
          </div>
        </section>

        <section class="dashboard-section dashboard-section--services-shop" aria-labelledby="services-shop-title">
          <button type="button" class="services-shop-card" @click="goToServices">
            <span class="services-shop-card__visual" aria-hidden="true">
              <img
                :src="servicesShopHeroImage"
                alt=""
                width="1600"
                height="666"
                loading="lazy"
                decoding="async"
              />
            </span>
            <span class="services-shop-card__copy">
              <span class="services-shop-card__eyebrow">
                <i aria-hidden="true"></i>
                Percorsi e servizi
              </span>
              <strong id="services-shop-title">
                <span>Confronta e</span>
                <em>acquista dall’app.</em>
              </strong>
              <small>Scopri le differenze tra Plus e TOP, scegli la durata o acquista Personal Training.</small>
              <span class="services-shop-card__action">
                Scopri i servizi
                <span class="material-icons" aria-hidden="true">arrow_forward</span>
              </span>
            </span>
          </button>
        </section>

        <section id="dashboard-path" class="dashboard-section" aria-labelledby="services-title">
          <div class="feature-grid">
            <div class="section-heading section-heading--services">
              <p class="section-kicker"><span></span> Tutto in un unico spazio</p>
              <div class="section-heading__copy">
                <h2 id="services-title">Il tuo percorso</h2>
                <p class="section-heading__description">Tutto ciò che ti serve per seguire e gestire il tuo percorso.</p>
              </div>
            </div>

            <article class="feature-card feature-card--operational operational-card--01" @click="goToProgrammi">
              <div class="feature-icon"><span class="material-icons" aria-hidden="true">route</span></div>
              <div class="feature-copy">
                <h3>I tuoi percorsi</h3>
                <p>Potrai visualizzare i tuoi percorsi e programmi.</p>
              </div>
              <span class="feature-arrow material-icons" aria-hidden="true">north_east</span>
            </article>

            <article class="feature-card feature-card--food feature-card--operational operational-card--02" @click="goToAlimentazione">
              <div class="feature-icon"><span class="material-icons" aria-hidden="true">dinner_dining</span></div>
              <div class="feature-copy">
                <h3>La tua alimentazione</h3>
                <p>Potrai visualizzare il tuo piano alimentare personalizzato.</p>
              </div>
              <span class="feature-arrow material-icons" aria-hidden="true">north_east</span>
            </article>

            <UserTrainingEntry class="feature-card feature-card--training feature-card--workout feature-card--operational operational-card--03" />

            <article class="feature-card feature-card--operational operational-card--04" @click="goToCheck">
              <div class="feature-icon"><span class="material-icons" aria-hidden="true">fact_check</span></div>
              <div class="feature-copy">
                <h3>I tuoi Check</h3>
                <p>Presto sarà disponibile il riepilogo di tutti i tuoi ordini.</p>
              </div>
              <span class="feature-arrow material-icons" aria-hidden="true">north_east</span>
            </article>

            <router-link to="/integrazione" class="feature-card feature-card--wide vitastrong-entry">
              <div class="feature-icon" aria-hidden="true"><img class="vitastrong-entry__logo" :src="vitastrongWolf" alt="" width="32" height="30" /></div>
              <div class="feature-copy">
                <h3>Integrazione by Vitastrong</h3>
                <p class="vitastrong-entry__description">Scopri i nostri bundle.</p>
              </div>
              <span class="feature-arrow material-icons" aria-hidden="true">arrow_forward</span>
            </router-link>

            <article class="feature-card feature-card--wide feature-card--recipe" @click="goToRicette">
              <div class="feature-icon"><span class="material-icons" aria-hidden="true">restaurant_menu</span></div>
              <div class="feature-copy">
                <h3>Crea una ricetta</h3>
                <p>Sei stanco delle solite ricette monotene, abbiamo messo il turbo con la nostra AI.</p>
              </div>
              <span class="feature-arrow material-icons" aria-hidden="true">arrow_forward</span>
            </article>
          </div>
        </section>
      </main>
    </div>

    <Transition name="premium-modal">
      <div
        v-if="premiumModalOpen"
        class="premium-modal-backdrop"
        role="presentation"
        @click.self="closePremiumModal"
      >
        <section
          class="premium-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="premium-modal-title"
          aria-describedby="premium-modal-description"
        >
          <button type="button" class="premium-modal__close" aria-label="Chiudi" @click="closePremiumModal">
            <span class="material-icons" aria-hidden="true">close</span>
          </button>

          <span class="premium-modal__icon material-icons" aria-hidden="true">lock</span>
          <span class="premium-modal__eyebrow">Accesso riservato</span>
          <h2 id="premium-modal-title">{{ premiumModalTitle }}</h2>
          <p id="premium-modal-description">
            Questo servizio è incluso esclusivamente in un percorso TOP attivo e pagato.
            Con un profilo Basic o Plus il collegamento resta bloccato.
          </p>
          <p
            v-if="premiumModalService === 'coaching'"
            id="premium-modal-contact-note"
            class="premium-modal__contact-note"
          >
            Non hai un percorso TOP attivo ma vuoi partecipare al Coaching Day?
            Contatta il team su WhatsApp.
          </p>
          <div class="premium-modal__actions">
            <a
              v-if="premiumModalService === 'coaching'"
              class="premium-modal__contact"
              :href="coachingDayWhatsappUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span class="material-icons" aria-hidden="true">chat</span>
              Contatta il team
            </a>
            <button type="button" class="premium-modal__confirm" @click="closePremiumModal">Ho capito</button>
          </div>
        </section>
      </div>
    </Transition>

  </div>
</template>


<script>
// Importa le librerie necessarie
import { useRouter } from 'vue-router';
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { getAuth, signOut, updatePassword } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import PopComponent from './PopComponent.vue';
import UserTrainingEntry from './UserTrainingEntry.vue';
import UserAppChrome from './UserAppChrome.vue';
import DashboardVideoCard from './DashboardVideoCard.vue';
import { resolveUserTechnicalTicket } from '@/lib/userTechnicalTicket';
import { isPaidOrder, orderHasTopProduct } from '@/lib/topSku';
import heroDashboardImage from '../../../fuscofit/assets/hero-coachingday-dashboard.jpg';
import supportDashboardImage from '../../../fuscofit/assets/support-dashboard.jpg';
import nutritionDashboardImage from '../../../fuscofit/assets/nutrition-dashboard.jpg';
import servicesShopHeroImage from '@/assets/services-shop-hero.jpg';
import vitastrongWolf from '../../../fuscofit/assets/vitastrong/red_wolf.png';

const preloadDashboardImages = () => {
  if (typeof document === 'undefined') return;

  [
    { href: heroDashboardImage, priority: 'high' },
    { href: supportDashboardImage, priority: 'high' },
    { href: nutritionDashboardImage, priority: 'high' },
  ].forEach(({ href, priority }) => {
    const absoluteHref = new URL(href, document.baseURI).href;
    const alreadyPreloaded = Array.from(
      document.querySelectorAll('link[rel="preload"][as="image"]')
    ).some((link) => link.href === absoluteHref);

    if (alreadyPreloaded) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    link.setAttribute('fetchpriority', priority);
    document.head.appendChild(link);
  });
};

export default {
  components: {
    PopComponent,
    UserTrainingEntry,
    DashboardVideoCard,
    UserAppChrome
  },
  
  setup() {
    preloadDashboardImages();

    const router = useRouter();
    const auth = getAuth();
    const showPopComponent = ref(false);
    const userTickets = ref([]);
    const newPassword = ref('');
    const confirmPassword = ref('');
    const passwordChangeError = ref('');
    const showChangePasswordFields = ref(false);
    const firestore = getFirestore();
    const studioName = ref('');
    const isTopProfile = ref(false);
    const premiumModalOpen = ref(false);
    const premiumModalTitle = ref('Servizio riservato ai profili TOP');
    const premiumModalService = ref('');
    const coachingDayUrl = 'https://www.metodofusco.it/tickets/#/prenota?service=consulenza&source=fuscofit';
    const topSupportWhatsappUrl = `https://wa.me/393784024837?text=${encodeURIComponent('Ciao Giampaolo, avrei bisogno di assistenza per il mio percorso TOP.')}`;
    const coachingDayWhatsappUrl = `https://wa.me/393279136921?text=${encodeURIComponent('Ciao Giampaolo, non ho un percorso TOP attivo ma vorrei partecipare al Coaching Day. Posso ricevere maggiori informazioni?')}`;

    const resolveTopProfileAccess = async (user, profileData = {}) => {
      const visibilityOverride = String(profileData.topListVisibilityOverride || '').trim().toLowerCase();
      if (visibilityOverride === 'archive') return false;
      if (visibilityOverride === 'active') return true;

      const uid = String(user?.uid || '').trim();
      const emails = Array.from(new Set([
        user?.email,
        profileData.email,
      ].map((value) => String(value || '').trim()).filter(Boolean)));
      const lookupPairs = [
        ['uid', uid],
        ['userUid', uid],
        ['userId', uid],
        ...emails.flatMap((email) => [
          ['userEmail', email],
          ['customerEmail', email],
          ['customer_email', email],
          ['email', email],
          ['billing.email', email],
        ]),
      ].filter(([, value]) => value);
      const uniqueLookups = Array.from(
        new Map(lookupPairs.map(([field, value]) => [`${field}:${value}`, [field, value]])).values()
      );

      const ordersRef = collection(firestore, 'orders');
      const results = await Promise.allSettled(
        uniqueLookups.map(([field, value]) => getDocs(query(ordersRef, where(field, '==', value))))
      );
      const orders = new Map();
      results.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        result.value.forEach((orderDoc) => {
          orders.set(orderDoc.id, orderDoc.data() || {});
        });
      });

      return Array.from(orders.values()).some((order) =>
        isPaidOrder(order) && (order.topSkuHit === true || orderHasTopProduct(order))
      );
    };

    let topAccessUser = null;
    let topAccessProfileData = null;
    let topAccessRefreshInFlight = false;

    const refreshTopProfileAccess = async () => {
      if (!topAccessUser || topAccessRefreshInFlight) return;
      topAccessRefreshInFlight = true;

      try {
        isTopProfile.value = await resolveTopProfileAccess(topAccessUser, topAccessProfileData || {});
      } catch (error) {
        console.warn('Impossibile aggiornare il profilo TOP:', error);
      } finally {
        topAccessRefreshInFlight = false;
      }
    };

    const handleTopAccessRefresh = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refreshTopProfileAccess();
    };


    const fetchStudioDetails = async (parentId) => {
  if (parentId === undefined) {
    console.error("parentId is undefined");
    return;  // Interrompe l'esecuzione della funzione
  }
    const studiCollectionRef = collection(firestore, 'studi');
  const q = query(studiCollectionRef, where("parent_id", "==", parentId));

  try {
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      // Assumendo che ci sia solo un documento con questo parent_id
      const studioDoc = querySnapshot.docs[0];
      studioName.value = studioDoc.data().name;
    } else {
      console.log("Studio non trovato con parent_id:", parentId);
    }
  } catch (error) {
    console.error("Errore nel recupero dei dettagli dello studio:", error);
  }
};




    const changePassword = async () => {
      if (newPassword.value !== confirmPassword.value) {
        passwordChangeError.value = 'Le password non corrispondono.';
        return;
      }

      // Aggiungi qui ulteriori controlli sulla sicurezza della password se necessario

      const user = auth.currentUser;
      try {
        await updatePassword(user, newPassword.value);
        console.log("Password cambiata con successo");
        // Reset delle password dopo il cambio
        newPassword.value = '';
        confirmPassword.value = '';
        showChangePasswordFields.value = false;
        passwordChangeError.value = '';
      } catch (error) {
        console.error("Errore durante il cambio della password:", error);
        passwordChangeError.value = "Errore nel cambio della password. Riprova.";
      }
    };

    const toggleChangePasswordFields = () => {
      showChangePasswordFields.value = !showChangePasswordFields.value;
    };


    const viewAlimentazione = async () => {
      // Il tuo codice esistente per viewAlimentazione
      // ...
    };

    const goToAlimentazione = () => {
      router.push('/alimentazione-page');  // Usa l'istanza del router
    };


    const goToCheck = () => {
      router.push('/check-storico');  // Usa l'istanza del router
    };

    const goToCreateTicket = () => {
      router.push('/create-ticket');  // Usa l'istanza del router
    };

    const goToVideoCall = () => {
      router.push('/video-call-page'); // Indirizza al componente RicetteComponent.vue
    };

    const goToRicette = () => {
      router.push('/ricetta'); // Indirizza al componente RicetteComponent.vue
    };

    const goToTickets = () => {
      router.push('/ticket-page'); // Indirizza al componente RicetteComponent.vue
    };

    const goToTicketsTecnico = async () => {
      const currentUserId = auth.currentUser?.uid;
      if (!currentUserId) {
        router.push('/ticket-page-tecnico');
        return;
      }

      try {
        const ticket = await resolveUserTechnicalTicket(firestore, currentUserId);
        if (ticket?.id) {
          router.push({
            name: 'UserTicketConversationTecnico',
            params: { ticketId: ticket.id },
          });
        } else {
          router.push('/ticket-page-tecnico');
        }
      } catch (error) {
        console.error('Errore apertura assistenza tecnica:', error);
        router.push('/ticket-page-tecnico');
      }
    };

    const closePremiumModal = () => {
      premiumModalOpen.value = false;
    };

    const handlePremiumService = async (service, event) => {
      if (!isTopProfile.value) {
        if (event) event.preventDefault();
        premiumModalService.value = service;
        premiumModalTitle.value = service === 'coaching'
          ? 'Coaching Day riservato ai profili TOP'
          : 'Assistenza riservata ai profili TOP';
        premiumModalOpen.value = true;
        return;
      }

      if (service === 'support') {
        window.open(topSupportWhatsappUrl, '_blank', 'noopener,noreferrer');
      }
    };


    const goToProgrammi = () => {
      router.push('/user-programmi'); // Indirizza al componente RicetteComponent.vue
    };

    const goToServices = () => {
      router.push('/servizi');
    };

    const goToGift = () => {
      router.push('/gift');
    };

    const goToComeFunziona = () => {
      router.push('/come-funziona');
    };



    const goToBooking = () => {
      router.push('/booking'); // Indirizza al componente RicetteComponent.vue
    };

    const scrollToSection = (sectionId) => {
      const target = document.getElementById(sectionId);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const viewConversation = (ticketId) => {
      router.push({ name: 'UserTicketConversation', params: { ticketId } });
    };
    
    const handleLogout = async () => {
      const auth = getAuth();
      try {
        await signOut(auth);
        router.push('/');
      } catch (error) {
        console.error("Errore durante il logout:", error);
      }
    };


onMounted(async () => {
  window.addEventListener('focus', handleTopAccessRefresh);
  document.addEventListener('visibilitychange', handleTopAccessRefresh);

  const currentUser = auth.currentUser;
  const userId = currentUser?.uid;
  if (currentUser && userId) {
    const userDocRef = doc(firestore, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      // Assumiamo che 'name' e 'surname' siano i campi da verificare
      if (!userData.name || !userData.surname) {
        showPopComponent.value = true; // Mostra il popup se il nome o il cognome non sono presenti
      }
      const parentId = userData.parent_id;
      topAccessUser = currentUser;
      topAccessProfileData = userData;
      const [, topAccess] = await Promise.all([
        fetchStudioDetails(parentId),
        resolveTopProfileAccess(currentUser, userData).catch((error) => {
          console.warn('Impossibile verificare il profilo TOP:', error);
          return false;
        }),
      ]);
      isTopProfile.value = topAccess;
    } else {
      console.log("Utente non trovato");
    }
  }
});

    onBeforeUnmount(() => {
      window.removeEventListener('focus', handleTopAccessRefresh);
      document.removeEventListener('visibilitychange', handleTopAccessRefresh);
    });

    onMounted(async () => {
      const requestedSection = router.currentRoute.value.query.section;
      const sectionId = requestedSection === 'support'
        ? 'dashboard-support'
        : requestedSection === 'path' ? 'dashboard-path' : '';
      if (!sectionId) return;
      await nextTick();
      window.requestAnimationFrame(() => scrollToSection(sectionId));
    });


    return {
      heroDashboardImage,
      servicesShopHeroImage,
      vitastrongWolf,
      userTickets,
      goToCreateTicket,
      viewConversation,
      goToRicette,
      viewAlimentazione,
      handleLogout,
      goToVideoCall,
      goToTickets,
      showPopComponent,
      goToAlimentazione,
      newPassword,
      confirmPassword,
      passwordChangeError,
      showChangePasswordFields,
      changePassword,
      toggleChangePasswordFields,
      studioName,
      isTopProfile,
      coachingDayUrl,
      premiumModalOpen,
      premiumModalTitle,
      premiumModalService,
      coachingDayWhatsappUrl,
      handlePremiumService,
      closePremiumModal,
      goToBooking,
      scrollToSection,
      goToTicketsTecnico,
      goToCheck,
      goToProgrammi,
      goToServices,
      goToGift,
      goToComeFunziona
    };
  },
};


</script>

  
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Montserrat:ital,wght@0,700;0,900;1,900&display=swap');

.user-dashboard {
  --ff-bg: #f5f4f0;
  --ff-surface: rgba(255, 255, 255, 0.72);
  --ff-white: #ffffff;
  --ff-black: #111110;
  --ff-gray-1: #1c1c1b;
  --ff-gray-2: #4a4a48;
  --ff-gray-3: #8a8a87;
  --ff-border: rgba(17, 17, 16, 0.1);
  --ff-red: #c8102e;
  --ff-red-dark: #9e0b23;
  min-height: 100vh;
  background:
    radial-gradient(circle at 87% 9%, rgba(200, 16, 46, 0.08), transparent 25rem),
    var(--ff-bg);
  color: var(--ff-black);
  font-family: "DM Sans", sans-serif;
  font-weight: 400;
  overflow-x: hidden;
}

.user-dashboard::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.72' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.025'/%3E%3C/svg%3E");
}

.dashboard-shell {
  position: relative;
  z-index: 1;
  width: min(100%, 1440px);
  margin: 0 auto;
}

.dashboard-main {
  padding: clamp(28px, 5vw, 72px) clamp(20px, 5vw, 72px) 140px;
}

.dashboard-hero,
.dashboard-section {
  scroll-margin-top: 76px;
}

.dashboard-hero {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 400px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 24px;
  background: var(--ff-black);
  color: var(--ff-white);
  box-shadow: 0 26px 76px rgba(17, 17, 16, 0.2);
  overflow: hidden;
}

.dashboard-hero::after {
  content: "";
  position: absolute;
  z-index: 3;
  right: clamp(22px, 4vw, 52px);
  bottom: 0;
  left: clamp(22px, 4vw, 52px);
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.26), rgba(255, 255, 255, 0.04));
  pointer-events: none;
}

.hero-copy {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: min(670px, 67%);
  padding: clamp(34px, 5vw, 68px);
}

.hero-copy .section-kicker {
  color: rgba(255, 255, 255, 0.7);
}

.hero-copy .section-kicker span {
  width: 28px;
  height: 2px;
  border-radius: 0;
  box-shadow: none;
}

.section-kicker {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
  color: var(--ff-gray-2);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.section-kicker span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ff-red);
  box-shadow: 0 0 0 5px rgba(200, 16, 46, 0.09);
}

.hero-copy h1 {
  margin: 0;
  color: var(--ff-red);
  font-family: "Montserrat", sans-serif;
  font-size: clamp(3.2rem, 6.2vw, 6.7rem);
  font-style: italic;
  font-weight: 900;
  letter-spacing: -0.055em;
  line-height: 0.84;
  text-transform: uppercase;
  text-shadow: 0 12px 36px rgba(0, 0, 0, 0.28);
}

.hero-copy h1 span {
  display: block;
  margin-bottom: 10px;
  color: var(--ff-white);
  font-size: 0.43em;
  letter-spacing: -0.035em;
  line-height: 1;
}

.account-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: clamp(34px, 6vw, 54px);
}

.account-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 44px;
  margin: 0;
  padding: 11px 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 999px;
  background: rgba(17, 17, 16, 0.34);
  color: var(--ff-white);
  font-family: "DM Sans", sans-serif;
  font-size: var(--ff-type-label);
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  cursor: pointer;
  backdrop-filter: blur(16px) saturate(1.3);
  -webkit-backdrop-filter: blur(16px) saturate(1.3);
  transition: transform 0.2s, color 0.2s, border-color 0.2s, background 0.2s;
}

.account-button--primary {
  border-color: var(--ff-red);
  background: var(--ff-red);
  color: var(--ff-white);
}

.account-button:hover {
  border-color: rgba(255, 255, 255, 0.58);
  background: rgba(255, 255, 255, 0.12);
  color: var(--ff-white);
  transform: translateY(-2px);
}

.account-button .material-icons {
  color: currentColor;
  font-size: 18px;
}

.hero-visual {
  position: absolute;
  z-index: 0;
  inset: 0;
  overflow: hidden;
}

.hero-visual img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: right center;
  filter: grayscale(12%);
  transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

.dashboard-hero:hover .hero-visual img {
  transform: scale(1.025);
}

.hero-visual__shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(17, 17, 16, 0.94) 0%, rgba(17, 17, 16, 0.74) 42%, rgba(17, 17, 16, 0.2) 78%),
    linear-gradient(0deg, rgba(17, 17, 16, 0.86) 0%, rgba(17, 17, 16, 0.08) 42%, rgba(17, 17, 16, 0.4) 100%);
}

.password-panel {
  display: grid;
  grid-template-columns: minmax(210px, 0.72fr) minmax(0, 1.5fr);
  gap: 24px;
  align-items: center;
  margin-top: 18px;
  padding: 24px;
  border: 1px solid var(--ff-border);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 14px 48px rgba(17, 17, 16, 0.07);
}

.password-panel__heading {
  display: flex;
  align-items: center;
  gap: 14px;
}

.password-panel__heading > .material-icons {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: rgba(200, 16, 46, 0.09);
  color: var(--ff-red);
}

.password-panel__eyebrow {
  color: var(--ff-red);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.password-panel h2 {
  margin: 3px 0 0;
  color: var(--ff-black);
  font-family: "Montserrat", sans-serif;
  font-size: 1rem;
  line-height: 1.2;
  text-align: left;
}

.password-panel__form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
  width: 100%;
  min-width: 0;
  gap: 10px;
}

.password-panel input {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  min-height: 46px;
  padding: 11px 15px;
  border: 1px solid var(--ff-border);
  border-radius: 12px;
  outline: none;
  background: var(--ff-bg);
  color: var(--ff-black);
  font-family: "DM Sans", sans-serif;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.password-panel input:focus {
  border-color: var(--ff-red);
  box-shadow: 0 0 0 3px rgba(200, 16, 46, 0.09);
}

.confirm-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 46px;
  max-width: 100%;
  padding: 11px 18px;
  border: 0;
  border-radius: 12px;
  background: var(--ff-black);
  color: var(--ff-white);
  font-family: "DM Sans", sans-serif;
  font-size: var(--ff-type-label);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
}

.confirm-button .material-icons {
  color: currentColor;
  font-size: 17px;
}

.password-error {
  grid-column: 2;
  margin-top: -12px;
  color: var(--ff-red-dark);
  font-size: 0.8rem;
  font-weight: 600;
}

.dashboard-section {
  padding-top: clamp(70px, 10vw, 124px);
}

.dashboard-section--support {
  position: relative;
  padding-top: clamp(64px, 8vw, 96px);
  isolation: isolate;
}

.dashboard-section--support::before {
  content: "";
  position: absolute;
  z-index: -1;
  top: 42%;
  right: -4%;
  width: min(430px, 68vw);
  height: 230px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(200, 16, 46, 0.14), rgba(200, 16, 46, 0) 68%);
  filter: blur(24px);
  pointer-events: none;
}

.section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 26px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--ff-border);
}

.section-heading .section-kicker {
  margin: 0 0 5px;
}

.section-heading h2 {
  margin: 0;
  color: var(--ff-black);
  font-family: "Montserrat", sans-serif;
  font-size: clamp(2rem, 4vw, 4.3rem);
  font-weight: 900;
  letter-spacing: -0.055em;
  line-height: 0.9;
  text-align: right;
  text-transform: uppercase;
}

.section-heading__copy {
  max-width: 560px;
  text-align: right;
}

.section-heading__description {
  margin: 10px 0 0;
  color: var(--ff-gray-2);
  font-size: clamp(var(--ff-type-body-small), 1.15vw, 0.92rem);
  line-height: 1.45;
}

.support-grid,
.feature-grid {
  display: grid;
  gap: 14px;
}

.support-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.feature-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.feature-card--featured,
.section-heading--services {
  grid-column: 1 / -1;
}

.section-heading--services {
  margin-top: 0;
}

.support-card,
.feature-card {
  position: relative;
  display: grid;
  align-items: center;
  gap: 18px;
  margin: 0;
  cursor: pointer;
  text-decoration: none;
  animation: dashboard-card-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
  transition: transform 0.25s, border-color 0.25s, box-shadow 0.25s, background 0.25s;
}

.support-card:nth-child(2),
.feature-card:nth-child(2) {
  animation-delay: 0.05s;
}

.feature-card:nth-child(3) {
  animation-delay: 0.1s;
}

.feature-card:nth-child(4) {
  animation-delay: 0.15s;
}

.feature-card:nth-child(5) {
  animation-delay: 0.2s;
}

.feature-card:nth-child(6) {
  animation-delay: 0.25s;
}

@keyframes dashboard-card-in {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.985);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.support-card {
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 156px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 18px;
  background-color: rgba(17, 17, 16, 0.7);
  background-image:
    linear-gradient(145deg, rgba(28, 28, 27, 0.82), rgba(17, 17, 16, 0.64)),
    var(--card-image, none);
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  color: var(--ff-white);
  box-shadow:
    0 20px 54px rgba(17, 17, 16, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    inset 0 -1px 0 rgba(255, 255, 255, 0.03);
  overflow: hidden;
  backdrop-filter: blur(26px) saturate(1.35);
  -webkit-backdrop-filter: blur(26px) saturate(1.35);
}

.support-card--technical {
  --card-image: url("../../../fuscofit/assets/support-dashboard.jpg");
  background-position: center 42%;
}

.support-card--nutrition {
  --card-image: url("../../../fuscofit/assets/nutrition-dashboard.jpg");
}

.support-card--top {
  --card-image: url("../../../fuscofit/assets/support-dashboard.jpg");
  grid-column: 1 / -1;
  min-height: 132px;
  border-color: rgba(227, 190, 91, 0.56);
  background-color: rgba(66, 43, 6, 0.78);
  background-image:
    linear-gradient(105deg, rgba(39, 27, 7, 0.96) 0%, rgba(116, 78, 12, 0.83) 48%, rgba(218, 176, 67, 0.5) 100%),
    var(--card-image, none);
  background-position: center 38%;
  box-shadow:
    0 20px 54px rgba(81, 56, 9, 0.2),
    inset 0 1px 0 rgba(255, 236, 177, 0.34),
    inset 0 -1px 0 rgba(255, 255, 255, 0.05);
  cursor: default;
}

.support-card--top::before {
  background: linear-gradient(180deg, #fff1b9, #d7a52d 52%, #8d5d09);
  box-shadow: 0 0 26px rgba(224, 177, 60, 0.58);
}

.support-card--top:hover,
.support-card--top:active {
  border-color: rgba(244, 211, 122, 0.7);
  box-shadow:
    0 22px 58px rgba(81, 56, 9, 0.24),
    inset 0 1px 0 rgba(255, 236, 177, 0.42);
  transform: none;
}

.support-card--top .feature-icon {
  border-color: rgba(255, 235, 171, 0.48);
  background: rgba(255, 228, 145, 0.14);
  color: #ffe69c;
}

.support-card--top .feature-copy h3 {
  max-width: none;
  color: var(--ff-white);
}

.support-top-label {
  display: block;
  margin-bottom: 6px;
  color: #ffe69c;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.support-top-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  min-height: 28px;
  padding: 6px 11px;
  border: 1px solid rgba(255, 239, 190, 0.56);
  border-radius: 999px;
  background: rgba(255, 230, 156, 0.14);
  color: #fff0b8;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}

.dashboard-section--premium {
  padding-top: clamp(34px, 5vw, 58px);
}

.premium-cluster {
  position: relative;
  isolation: isolate;
  padding: 26px;
  border: 1px solid rgba(237, 198, 94, 0.66);
  border-radius: 28px;
  background:
    radial-gradient(circle at 92% 0%, rgba(241, 205, 105, 0.19), transparent 31%),
    linear-gradient(145deg, #171611 0%, #262113 56%, #171611 100%);
  color: var(--ff-white);
  box-shadow:
    0 28px 74px rgba(40, 31, 10, 0.22),
    inset 0 0 0 1px rgba(255, 242, 196, 0.08);
  overflow: hidden;
}

.premium-cluster:not(.premium-cluster--locked) {
  border-color: rgba(111, 73, 8, 0.52);
  background:
    radial-gradient(circle at 92% 0%, rgba(255, 246, 199, 0.52), transparent 32%),
    linear-gradient(135deg, #e5c66d 0%, #b88825 50%, #e8cb76 100%);
  box-shadow:
    0 28px 74px rgba(99, 66, 10, 0.24),
    inset 0 0 0 1px rgba(255, 249, 219, 0.38);
}

.premium-cluster::before {
  content: "";
  position: absolute;
  z-index: -1;
  inset: 0;
  background:
    linear-gradient(90deg, transparent, rgba(255, 227, 147, 0.7), transparent) top / 56% 1px no-repeat,
    repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.018) 0 1px, transparent 1px 12px);
  pointer-events: none;
}

.premium-cluster::after {
  content: "";
  position: absolute;
  z-index: -1;
  right: -104px;
  bottom: -126px;
  width: 340px;
  height: 340px;
  border: 1px solid rgba(255, 229, 153, 0.12);
  border-radius: 50%;
  box-shadow: 0 0 0 40px rgba(255, 229, 153, 0.025), 0 0 0 82px rgba(255, 229, 153, 0.018);
  pointer-events: none;
}

.premium-cluster__heading {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  margin-bottom: 22px;
  text-align: left;
}

.premium-cluster__heading > div {
  min-width: 0;
  text-align: left;
}

.premium-cluster__icon,
.premium-card__icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border: 1px solid rgba(255, 235, 171, 0.4);
  background: rgba(255, 231, 159, 0.12);
  color: #ffe49a;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

.premium-cluster__icon {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  font-size: 25px;
}

.premium-cluster__eyebrow,
.premium-card__copy > span {
  display: block;
  color: #ffe49a;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.premium-cluster__heading h2 {
  margin: 0;
  color: #f5d476;
  font-family: "Montserrat", sans-serif;
  font-size: clamp(1.6rem, 3.1vw, 2.8rem);
  font-weight: 900;
  letter-spacing: -0.045em;
  line-height: 0.95;
  text-align: left;
  text-transform: uppercase;
}

.premium-cluster:not(.premium-cluster--locked) .premium-cluster__heading h2 {
  color: #1c170d;
  text-shadow: none;
}

.premium-cluster__heading p {
  margin: 7px 0 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: var(--ff-type-body-small);
  line-height: 1.45;
}

.premium-cluster:not(.premium-cluster--locked) .premium-cluster__heading p {
  color: rgba(28, 23, 13, 0.76);
}

.premium-cluster__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-width: 58px;
  min-height: 34px;
  padding: 7px 12px;
  border: 1px solid rgba(255, 239, 190, 0.58);
  border-radius: 999px;
  background: #f1cf6d;
  color: #17130b;
  color: #fff0b8;
  font-size: 0.6875rem;
  font-weight: 900;
  letter-spacing: 0.16em;
}

.premium-cluster__badge .material-icons {
  color: currentColor;
  font-size: 13px;
}

.premium-cluster__badge--locked {
  border-color: rgba(255, 231, 158, 0.3);
  background: rgba(255, 230, 156, 0.09);
  color: rgba(255, 235, 178, 0.82);
}

.premium-cluster:not(.premium-cluster--locked) .premium-cluster__badge {
  border-color: rgba(54, 39, 9, 0.34);
  background: rgba(255, 240, 189, 0.78);
  color: #1b160b !important;
}

.premium-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
}

.premium-card {
  position: relative;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  min-height: 98px;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  padding: 20px;
  border: 1px solid rgba(246, 216, 132, 0.42);
  border-radius: 20px;
  background-color: #302714;
  background-image:
    radial-gradient(circle at 100% 0%, rgba(255, 222, 126, 0.2), transparent 45%),
    linear-gradient(135deg, rgba(154, 105, 18, 0.34), rgba(75, 56, 20, 0.26) 56%, rgba(255, 220, 122, 0.08));
  color: var(--ff-white);
  font: inherit;
  text-align: left;
  text-decoration: none;
  box-shadow:
    0 14px 34px rgba(0, 0, 0, 0.2),
    inset 0 0 0 1px rgba(255, 255, 255, 0.025);
  overflow: hidden;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  transition: transform 0.24s, border-color 0.24s, box-shadow 0.24s;
}

.premium-card::before {
  position: absolute;
  top: 50%;
  right: 58px;
  color: rgba(246, 216, 132, 0.09);
  font-family: "Montserrat", sans-serif;
  font-size: 3.45rem;
  font-style: italic;
  font-weight: 900;
  letter-spacing: -0.08em;
  line-height: 1;
  pointer-events: none;
  transform: translateY(-50%);
}

.premium-card::after {
  content: "";
  position: absolute;
  right: 14px;
  bottom: 0;
  left: 14px;
  height: 3px;
  border-radius: 999px 999px 0 0;
  background: linear-gradient(90deg, transparent, #b37c15 20%, #ffe39a 80%, transparent);
  box-shadow: 0 -5px 18px rgba(231, 185, 75, 0.22);
}

.premium-card--support {
  --premium-index: "01";
}

.premium-card--coaching {
  --premium-index: "02";
}

.premium-card--support::before,
.premium-card--coaching::before {
  content: var(--premium-index);
}

.premium-card:hover {
  border-color: rgba(255, 238, 186, 0.52);
  box-shadow:
    0 20px 44px rgba(17, 12, 3, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transform: translateY(-3px);
}

.premium-card:active {
  transform: scale(0.98);
}

.premium-card__icon {
  width: 48px;
  height: 48px;
  border-radius: 15px;
}

.premium-card__icon .material-icons {
  color: currentColor;
  font-size: 22px;
}

.premium-card__copy h3 {
  max-width: 32ch;
  margin: 5px 0 0;
  color: var(--ff-white);
  font-family: "Montserrat", sans-serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.15;
}

.premium-card__arrow {
  color: #ffe49a;
  font-size: 21px;
  transition: transform 0.2s;
}

.premium-card:hover .premium-card__arrow {
  transform: translate(3px, -3px);
}

.premium-card--locked {
  border-color: rgba(240, 202, 101, 0.34);
  background-color: #302714;
  background-image:
    radial-gradient(circle at 100% 0%, rgba(255, 222, 126, 0.13), transparent 43%),
    linear-gradient(135deg, rgba(133, 91, 16, 0.3), rgba(52, 45, 28, 0.42) 58%, rgba(112, 79, 18, 0.2));
}

.premium-card--locked .premium-card__icon {
  border-color: rgba(255, 224, 137, 0.48);
  background: rgba(214, 164, 48, 0.14);
  color: #f6d983;
}

.premium-card--locked .premium-card__copy > span,
.premium-card--locked .premium-card__arrow {
  color: rgba(255, 227, 151, 0.76);
}

.premium-card--locked::after {
  background: linear-gradient(90deg, transparent, rgba(255, 229, 153, 0.2) 20%, rgba(255, 229, 153, 0.48) 80%, transparent);
  box-shadow: 0 -5px 18px rgba(255, 229, 153, 0.08);
}

.premium-card--locked:hover {
  border-color: rgba(255, 229, 153, 0.62);
  box-shadow:
    0 18px 42px rgba(97, 66, 10, 0.24),
    inset 0 0 0 1px rgba(255, 237, 179, 0.06);
}

.premium-card--locked:hover .premium-card__arrow {
  transform: none;
}

.dashboard-section--services-shop {
  padding-top: clamp(24px, 4vw, 42px);
}

.services-shop-card {
  position: relative;
  isolation: isolate;
  display: block;
  width: 100%;
  min-height: 220px;
  padding: 26px 28px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  background: #111110;
  color: #fff;
  text-align: left;
  box-shadow: 0 20px 54px rgba(17, 17, 16, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.12);
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
}

.services-shop-card::before {
  content: "";
  position: absolute;
  z-index: 1;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(10, 10, 9, 0.98) 0%, rgba(10, 10, 9, 0.9) 37%, rgba(10, 10, 9, 0.56) 58%, rgba(10, 10, 9, 0.1) 82%),
    linear-gradient(0deg, rgba(10, 10, 9, 0.52), transparent 58%);
  pointer-events: none;
}

.services-shop-card::after {
  content: "";
  position: absolute;
  z-index: 3;
  top: 0;
  right: 0;
  bottom: 0;
  width: 4px;
  background: var(--ff-red);
  pointer-events: none;
}

.services-shop-card:hover {
  border-color: rgba(255, 115, 137, 0.5);
  box-shadow: 0 24px 60px rgba(17, 17, 16, 0.21), inset 0 1px 0 rgba(255, 255, 255, 0.15);
  transform: translateY(-3px);
}

.services-shop-card__visual {
  position: absolute;
  z-index: 0;
  inset: 0;
}

.services-shop-card__visual img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 62% center;
}

.services-shop-card__copy {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  max-width: min(62%, 620px);
}

.services-shop-card__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.services-shop-card__eyebrow i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ff-red);
  box-shadow: 0 0 0 5px rgba(200, 16, 46, 0.16);
}

.services-shop-card__copy strong {
  display: flex;
  flex-direction: column;
  margin-top: 5px;
  font-family: "Montserrat", sans-serif;
  font-size: clamp(1.7rem, 3.5vw, 2.8rem);
  font-weight: 900;
  letter-spacing: -0.055em;
  line-height: 0.94;
  text-transform: uppercase;
}

.services-shop-card__copy strong span {
  color: rgba(255, 255, 255, 0.94);
}

.services-shop-card__copy strong em {
  color: #ef2345;
  font-style: normal;
}

.services-shop-card__copy small {
  max-width: 46ch;
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.78rem;
  line-height: 1.4;
}

.services-shop-card__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 42px;
  margin-top: 16px;
  padding: 9px 15px;
  border: 0;
  border-radius: 999px;
  background: var(--ff-red);
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  box-shadow: 0 10px 26px rgba(200, 16, 46, 0.28);
  text-transform: uppercase;
  white-space: nowrap;
}

.services-shop-card__action .material-icons {
  color: #fff !important;
  font-size: 17px;
}

.premium-modal-backdrop {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  padding:
    max(20px, env(safe-area-inset-top))
    max(20px, env(safe-area-inset-right))
    max(20px, env(safe-area-inset-bottom))
    max(20px, env(safe-area-inset-left));
  background: rgba(17, 17, 16, 0.52);
  backdrop-filter: blur(12px) saturate(0.9);
  -webkit-backdrop-filter: blur(12px) saturate(0.9);
}

.premium-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  max-width: 380px;
  max-height: calc(100dvh - 40px);
  padding: 28px;
  border: 1px solid rgba(255, 229, 153, 0.5);
  border-radius: 24px;
  background:
    radial-gradient(circle at 100% 0, rgba(224, 177, 60, 0.24), transparent 44%),
    rgba(25, 22, 17, 0.92);
  color: #fff;
  box-shadow:
    0 28px 90px rgba(0, 0, 0, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.18);
  overflow-x: hidden;
  overflow-y: auto;
  text-align: center;
  backdrop-filter: blur(28px) saturate(1.2);
  -webkit-backdrop-filter: blur(28px) saturate(1.2);
}

.premium-modal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  cursor: pointer;
}

.premium-modal__close .material-icons {
  color: currentColor;
  font-size: 18px;
}

.premium-modal__icon {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  margin: 0 auto 18px;
  border: 1px solid rgba(255, 229, 153, 0.5);
  border-radius: 17px;
  background: rgba(255, 225, 139, 0.12);
  color: #ffe49a;
  font-size: 24px;
}

.premium-modal__eyebrow {
  display: block;
  width: 100%;
  color: #ffe49a;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.premium-modal h2 {
  width: 100%;
  max-width: 310px;
  margin: 7px auto 10px;
  color: #fff;
  font-family: "Montserrat", sans-serif;
  font-size: 1.42rem;
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 1.05;
  text-transform: uppercase;
}

.premium-modal p {
  width: 100%;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.88rem;
  line-height: 1.55;
}

.premium-modal p.premium-modal__contact-note {
  box-sizing: border-box;
  max-width: 100%;
  margin-top: 18px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 229, 153, 0.18);
  border-radius: 14px;
  background: rgba(255, 225, 139, 0.07);
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.76rem;
  line-height: 1.45;
}

.premium-modal__actions {
  display: grid;
  gap: 10px;
  width: 100%;
  margin-top: 20px;
}

.premium-modal__contact,
.premium-modal__confirm {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 46px;
  margin: 0;
  padding: 10px 18px;
  font-family: "DM Sans", sans-serif;
  font-size: var(--ff-type-label);
  font-weight: 800;
  letter-spacing: 0.1em;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  cursor: pointer;
}

.premium-modal__contact {
  gap: 9px;
  border: 1px solid rgba(104, 218, 143, 0.5);
  border-radius: 999px;
  background: rgba(37, 211, 102, 0.13);
  color: #a7f0bf;
}

.premium-modal__contact .material-icons {
  color: currentColor;
  font-size: 19px;
}

.premium-modal__confirm {
  border: 1px solid #d8a934;
  border-radius: 999px;
  background: linear-gradient(135deg, #f4d274, #b67b13);
  color: #17130b;
  box-shadow: 0 12px 30px rgba(181, 124, 20, 0.24);
}

@media (max-width: 480px) {
  .premium-modal-backdrop {
    padding:
      max(16px, env(safe-area-inset-top))
      max(16px, env(safe-area-inset-right))
      max(16px, env(safe-area-inset-bottom))
      max(16px, env(safe-area-inset-left));
  }

  .premium-modal {
    max-height: calc(100dvh - 32px);
    padding: 26px 20px 22px;
    border-radius: 22px;
  }

  .premium-modal h2 {
    max-width: 285px;
    font-size: 1.24rem;
    line-height: 1.08;
  }

  .premium-modal p {
    font-size: 0.84rem;
  }
}

.premium-modal-enter-active,
.premium-modal-leave-active {
  transition: opacity 0.2s ease;
}

.premium-modal-enter-active .premium-modal,
.premium-modal-leave-active .premium-modal {
  transition: transform 0.22s ease, opacity 0.2s ease;
}

.premium-modal-enter-from,
.premium-modal-leave-to {
  opacity: 0;
}

.premium-modal-enter-from .premium-modal,
.premium-modal-leave-to .premium-modal {
  opacity: 0;
  transform: translateY(12px) scale(0.97);
}

.support-card::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  border-radius: 18px 0 0 18px;
  background: linear-gradient(180deg, rgba(255, 64, 91, 0.98), var(--ff-red));
  box-shadow: 0 0 24px rgba(200, 16, 46, 0.34);
}

.support-card::after {
  content: "";
  position: absolute;
  top: 0;
  left: 12%;
  width: 54%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.72), transparent);
  opacity: 0.62;
  pointer-events: none;
}

.support-card:hover {
  transform: translateY(-4px);
  border-color: rgba(255, 255, 255, 0.34);
  box-shadow:
    0 24px 65px rgba(17, 17, 16, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.support-card:active,
.feature-card:active {
  transform: scale(0.975);
  transition-duration: 0.08s;
}

.feature-card {
  grid-template-columns: auto minmax(0, 1fr);
  min-height: 210px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.88);
  border-radius: 18px;
  background-color: rgba(255, 255, 255, 0.56);
  background-image:
    linear-gradient(145deg, rgba(255, 255, 255, 0.88), rgba(255, 255, 255, 0.62)),
    var(--card-image, none);
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  color: var(--ff-black);
  box-shadow:
    0 14px 38px rgba(17, 17, 16, 0.075),
    inset 0 1px 0 rgba(255, 255, 255, 0.96),
    inset 0 -1px 0 rgba(17, 17, 16, 0.025);
  overflow: hidden;
  backdrop-filter: blur(28px) saturate(1.45);
  -webkit-backdrop-filter: blur(28px) saturate(1.45);
}

.feature-card--coaching-day {
  --card-image: url("../../../fuscofit/assets/hero-coachingday-dashboard.jpg");
  background-position: right center;
}

.feature-card--food {
  --card-image: url("../../../fuscofit/assets/food-french-toast-italiano.jpg");
}

.feature-card--workout {
  --card-image: url("../../../fuscofit/assets/servizio allenamento da casa.png");
}

.vitastrong-entry {
  --card-image: url("../../assets/vitastrong-dashboard-natural.jpg");
}
.vitastrong-entry:focus-visible { outline: 3px solid var(--ff-red); outline-offset: 4px; }
.vitastrong-entry__logo { display: block; width: 32px; height: 30px; object-fit: contain; }
.feature-card.vitastrong-entry .feature-copy > .vitastrong-entry__description { display: block; margin: 6px 0 0; color: inherit; opacity: 0.8; }
@media (max-width: 700px) {
  .vitastrong-entry__logo { filter: brightness(0) invert(1); }
}

.feature-card--recipe {
  --card-image: url("../../../fuscofit/assets/recipe-dashboard.jpg");
  background-position: center 45%;
}

.feature-card:hover {
  border-color: rgba(255, 255, 255, 1);
  background-color: rgba(255, 255, 255, 0.72);
  background-image:
    linear-gradient(145deg, rgba(255, 255, 255, 0.94), rgba(255, 255, 255, 0.7)),
    var(--card-image, none);
  box-shadow:
    0 20px 52px rgba(17, 17, 16, 0.11),
    0 0 0 1px rgba(200, 16, 46, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 1);
  transform: translateY(-4px);
}

.feature-icon {
  display: grid;
  place-items: center;
  align-self: start;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.68);
  background: linear-gradient(145deg, rgba(200, 16, 46, 0.12), rgba(200, 16, 46, 0.055));
  color: var(--ff-red);
  box-shadow:
    0 8px 22px rgba(200, 16, 46, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.support-card .feature-icon {
  align-self: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.11), rgba(255, 255, 255, 0.035));
  color: var(--ff-white);
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.feature-icon .material-icons {
  color: currentColor;
  font-size: 23px;
}

.feature-copy {
  min-width: 0;
}

.feature-copy h3 {
  margin: 0 0 8px;
  color: inherit;
  font-family: "Montserrat", sans-serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.feature-copy p {
  margin: 0;
  color: var(--ff-gray-2);
  font-size: var(--ff-type-body-small);
  line-height: 1.55;
}

.support-card .feature-copy p {
  color: rgba(255, 255, 255, 0.63);
}

.feature-arrow {
  color: var(--ff-gray-3);
  font-size: 20px;
  transition: color 0.2s, transform 0.2s;
}

.feature-card > .feature-arrow {
  position: absolute;
  right: 22px;
  bottom: 21px;
}

.support-card .feature-arrow {
  color: var(--ff-white);
}

.support-card:hover .feature-arrow,
.feature-card:hover .feature-arrow {
  color: var(--ff-red);
  transform: translate(3px, -2px);
}

.feature-badge {
  position: absolute;
  top: 18px;
  right: 18px;
  padding: 6px 9px;
  border-radius: 999px;
  background: var(--ff-red);
  color: var(--ff-white);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.feature-grid .feature-card--training {
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 210px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.88);
  border-radius: 18px;
  background-color: rgba(255, 255, 255, 0.56);
  background-image:
    linear-gradient(145deg, rgba(255, 255, 255, 0.88), rgba(255, 255, 255, 0.62)),
    var(--card-image, none);
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  color: var(--ff-black);
  box-shadow:
    0 14px 38px rgba(17, 17, 16, 0.075),
    inset 0 1px 0 rgba(255, 255, 255, 0.96);
  overflow: hidden;
  backdrop-filter: blur(28px) saturate(1.45);
  -webkit-backdrop-filter: blur(28px) saturate(1.45);
}

.feature-grid .feature-card--training:hover {
  border-color: rgba(200, 16, 46, 0.36);
  background-color: rgba(255, 255, 255, 0.72);
  background-image:
    linear-gradient(145deg, rgba(255, 255, 255, 0.94), rgba(255, 255, 255, 0.7)),
    var(--card-image, none);
  color: var(--ff-black);
  box-shadow: 0 18px 50px rgba(17, 17, 16, 0.09);
  transform: translateY(-4px);
}

.feature-grid .feature-card--training.user-training-entry--disabled,
.feature-grid .feature-card--training.user-training-entry--disabled:hover {
  border-color: var(--ff-border) !important;
  background: rgba(236, 236, 234, 0.7) !important;
  color: var(--ff-gray-3) !important;
  box-shadow: none !important;
  transform: none !important;
}

.feature-grid .feature-card--training :deep(.icon-column) {
  display: grid;
  place-items: center;
  align-self: start;
  width: 50px;
  height: 50px;
  margin: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.68);
  background: linear-gradient(145deg, rgba(200, 16, 46, 0.12), rgba(200, 16, 46, 0.055));
  box-shadow:
    0 8px 22px rgba(200, 16, 46, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.feature-grid .feature-card--training :deep(.text-column) {
  align-self: start;
  min-width: 0;
  padding: 0;
}

.feature-grid .feature-card--training :deep(.header-text) {
  margin: 0 0 8px;
  color: var(--ff-black);
  font-family: "Montserrat", sans-serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.feature-grid .feature-card--training :deep(.text-card) {
  color: var(--ff-gray-2);
  font-family: "DM Sans", sans-serif;
  font-size: var(--ff-type-body-small);
  line-height: 1.55;
}

.feature-grid .feature-card--training :deep(.material-icons) {
  color: var(--ff-red);
}

.feature-grid .feature-card--training :deep(.icon-column-right) {
  position: absolute;
  right: 22px;
  bottom: 21px;
  margin: 0;
}

.feature-grid .feature-card--training :deep(.icon-column-right .material-icons) {
  color: var(--ff-gray-3);
  font-size: 20px;
}

.feature-card--operational {
  --card-image: none;
  --operational-accent: #c8102e;
  --operational-accent-rgb: 200, 16, 46;
  isolation: isolate;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 118px;
  padding: 20px;
  border-color: rgba(17, 17, 16, 0.08);
  border-radius: 20px;
  background-color: rgba(255, 255, 255, 0.8);
  background-image:
    radial-gradient(circle at 0 0, rgba(var(--operational-accent-rgb), 0.1), transparent 44%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(246, 244, 241, 0.9));
  color: var(--ff-black);
  box-shadow:
    0 14px 36px rgba(17, 17, 16, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.96);
}

.feature-card--operational::before {
  content: none;
}

.feature-card--operational::after {
  content: "";
  position: absolute;
  z-index: 2;
  top: 20px;
  bottom: 20px;
  left: 0;
  width: 3px;
  border-radius: 0 999px 999px 0;
  background: var(--operational-accent);
  box-shadow: 5px 0 20px rgba(var(--operational-accent-rgb), 0.22);
  pointer-events: none;
}

.feature-card--operational > * {
  position: relative;
  z-index: 1;
}

.feature-card--operational .feature-copy,
.feature-grid .feature-card--training.feature-card--operational :deep(.text-column) {
  align-self: center;
  min-width: 0;
}

.feature-card--operational .feature-copy h3,
.feature-grid .feature-card--training.feature-card--operational :deep(.header-text) {
  display: block;
  min-height: 0;
  margin: 0;
  color: var(--ff-black);
  font-size: 1rem;
  letter-spacing: -0.025em;
  line-height: 1.15;
}

.feature-card--operational .feature-copy p,
.feature-grid .feature-card--training.feature-card--operational :deep(.text-card) {
  margin-top: 5px;
  color: var(--ff-gray-2);
  font-size: 0.75rem;
  line-height: 1.35;
}

.operational-card--02 {
  --operational-accent: #e5693f;
  --operational-accent-rgb: 229, 105, 63;
}

.operational-card--03 {
  --operational-accent: #ad2440;
  --operational-accent-rgb: 173, 36, 64;
}

.operational-card--04 {
  --operational-accent: #d83b59;
  --operational-accent-rgb: 216, 59, 89;
}

.feature-card--operational .feature-icon,
.feature-grid .feature-card--training.feature-card--operational :deep(.icon-column) {
  width: 48px;
  height: 48px;
  margin: 0;
  border-color: rgba(var(--operational-accent-rgb), 0.2);
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(var(--operational-accent-rgb), 0.16), rgba(var(--operational-accent-rgb), 0.045));
  color: var(--operational-accent);
  box-shadow:
    0 10px 24px rgba(var(--operational-accent-rgb), 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.88);
}

.feature-card--operational > .feature-arrow,
.feature-grid .feature-card--training.feature-card--operational :deep(.icon-column-right) {
  position: static;
  grid-column: 3;
  grid-row: 1;
  align-self: center;
  justify-self: end;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  margin: 0;
  border: 0;
  border-radius: 50%;
  background: var(--ff-black);
  color: var(--ff-white);
  box-shadow: 0 10px 22px rgba(17, 17, 16, 0.18);
}

.feature-grid .feature-card--training.feature-card--operational :deep(.icon-column-right .material-icons) {
  color: var(--ff-white);
  font-size: 18px;
}

.feature-card--operational:hover,
.feature-grid .feature-card--training.feature-card--operational:hover {
  border-color: rgba(var(--operational-accent-rgb), 0.28);
  background-color: rgba(255, 255, 255, 0.96);
  background-image:
    radial-gradient(circle at 0 0, rgba(var(--operational-accent-rgb), 0.14), transparent 48%),
    linear-gradient(145deg, #ffffff, #f8f6f3);
  color: var(--ff-black);
  box-shadow:
    0 20px 46px rgba(17, 17, 16, 0.12),
    0 0 0 1px rgba(var(--operational-accent-rgb), 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 1);
}

.feature-grid .feature-card--training.feature-card--operational.user-training-entry--disabled,
.feature-grid .feature-card--training.feature-card--operational.user-training-entry--disabled:hover {
  --operational-accent: #8c8b86;
  --operational-accent-rgb: 140, 139, 134;
  grid-template-columns: auto minmax(0, 1fr);
  border-color: rgba(17, 17, 16, 0.07) !important;
  background-color: rgba(238, 237, 233, 0.9) !important;
  background-image: linear-gradient(145deg, rgba(245, 244, 241, 0.96), rgba(226, 225, 221, 0.9)) !important;
  color: var(--ff-gray-2) !important;
  box-shadow:
    0 12px 30px rgba(17, 17, 16, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.8) !important;
  cursor: default;
}

.feature-grid .feature-card--training.feature-card--operational.user-training-entry--disabled :deep(.icon-column) {
  border-color: rgba(17, 17, 16, 0.08);
  background: rgba(17, 17, 16, 0.045);
  color: #777772;
  box-shadow: none;
}

.feature-grid .feature-card--training.feature-card--operational.user-training-entry--disabled :deep(.header-text) {
  color: #555550;
}

.feature-grid .feature-card--training.feature-card--operational.user-training-entry--disabled :deep(.text-card) {
  color: #777772;
}

@media (max-width: 980px) {
  .dashboard-hero {
    min-height: 375px;
  }

  .hero-copy {
    width: min(650px, 78%);
  }

  .password-panel {
    grid-template-columns: 1fr;
  }

  .password-error {
    grid-column: 1;
    margin-top: 0;
  }

  .feature-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .dashboard-main {
    padding: 18px 12px 138px;
  }

  .dashboard-hero {
    min-height: 357px;
    border-radius: 18px;
  }

  .hero-copy {
    justify-content: flex-end;
    width: 100%;
    min-height: 357px;
    padding: 24px;
  }

  .hero-copy h1 {
    max-width: 100%;
    font-size: min(clamp(2rem, 8.5vw, 3rem), 12vw);
    line-height: 0.95;
    overflow-wrap: anywhere;
  }

  .hero-copy .section-kicker {
    font-size: min(0.6875rem, 5vw);
  }

  .account-actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    margin-top: 24px;
  }

  .account-button {
    min-width: 0;
    width: 100%;
    padding-inline: 12px;
    font-size: min(var(--ff-type-label), 6vw);
    letter-spacing: 0.08em;
    line-height: 1.2;
    white-space: normal;
  }

  .account-button__label {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .account-button .material-icons {
    flex: 0 0 auto;
  }

  .hero-visual {
    min-height: 0;
  }

  .hero-visual img {
    object-position: right center;
  }

  .hero-visual__shade {
    background:
      linear-gradient(90deg, rgba(17, 17, 16, 0.8) 0%, rgba(17, 17, 16, 0.5) 58%, rgba(17, 17, 16, 0.2) 100%),
      linear-gradient(0deg, rgba(17, 17, 16, 0.96) 0%, rgba(17, 17, 16, 0.2) 58%, rgba(17, 17, 16, 0.42) 100%);
  }

  .password-panel {
    padding: 20px;
  }

  .password-panel__form {
    grid-template-columns: 1fr;
  }

  .dashboard-section,
  .dashboard-section--support {
    padding-top: 48px;
  }

  .section-heading {
    display: block;
    margin-bottom: 16px;
    padding-bottom: 0;
    border-bottom: 0;
  }

  .section-heading .section-kicker {
    display: none;
  }

  .section-heading h2 {
    margin: 0;
    font-size: min(clamp(1.45rem, 7vw, 2rem), 10vw);
    letter-spacing: -0.045em;
    line-height: 1;
    text-align: left;
    overflow-wrap: anywhere;
  }

  .section-heading__copy {
    max-width: 290px;
    text-align: left;
  }

  .section-heading__description {
    margin-top: 7px;
    font-size: var(--ff-type-body-small);
    line-height: 1.4;
  }

  .support-grid,
  .feature-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .support-card {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    align-content: start;
    min-height: 152px;
    padding: 15px;
    gap: 12px;
    border-radius: 16px;
  }

  .support-card::before {
    top: 15px;
    right: 15px;
    bottom: auto;
    left: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    box-shadow: 0 0 0 6px rgba(200, 16, 46, 0.08), 0 0 20px rgba(200, 16, 46, 0.42);
  }

  .support-card .feature-icon {
    align-self: start;
    width: 44px;
    height: 44px;
  }

  .support-card .feature-copy h3 {
    max-width: 11ch;
    margin: 0;
    font-size: min(var(--ff-type-body), 6vw);
    line-height: 1.16;
    overflow-wrap: anywhere;
  }

  .support-card .feature-copy {
    align-self: end;
    width: 100%;
    padding-right: 28px;
    padding-bottom: 1px;
  }

  .support-card .feature-copy p {
    display: none;
  }

  .support-card .feature-arrow {
    position: absolute;
    right: 14px;
    bottom: 13px;
    font-size: 18px;
  }

  .dashboard-section--premium {
    padding-top: 28px;
  }

  .premium-cluster {
    padding: 14px;
    border-radius: 18px;
  }

  .premium-cluster__heading {
    grid-template-columns: minmax(0, 1fr);
    gap: 11px;
    margin-bottom: 14px;
    padding-right: 44px;
  }

  .premium-cluster__icon {
    width: 42px;
    height: 42px;
    border-radius: 13px;
    font-size: 21px;
  }

  .premium-cluster__eyebrow {
    font-size: 0.6875rem;
  }

  .premium-cluster__heading h2 {
    font-size: min(1.25rem, 8vw);
    overflow-wrap: anywhere;
  }

  .premium-cluster__heading p {
    max-width: 230px;
    margin-top: 5px;
    font-size: var(--ff-type-body-small);
    line-height: 1.35;
  }

  .premium-cluster__badge {
    position: absolute;
    top: 0;
    right: 0;
    min-width: 39px;
    min-height: 25px;
    padding: 5px 7px;
    font-size: 0.6875rem;
  }

  .premium-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }

  .premium-card {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-rows: 1fr;
    align-content: center;
    align-items: center;
    min-height: 88px;
    padding: 13px 14px;
    gap: 12px;
    border-radius: 15px;
  }

  .premium-card::before {
    top: 50%;
    right: 45px;
    font-size: 2.9rem;
    transform: translateY(-50%);
  }

  .premium-card__icon {
    width: 40px;
    height: 40px;
  }

  .premium-card__icon .material-icons {
    font-size: 20px;
  }

  .premium-card__copy {
    align-self: center;
  }

  .premium-card__copy > span {
    font-size: min(0.6875rem, 5vw);
    letter-spacing: 0.11em;
    overflow-wrap: anywhere;
  }

  .premium-card__copy h3 {
    max-width: 25ch;
    font-size: min(var(--ff-type-body-small), 6vw);
    line-height: 1.16;
    overflow-wrap: anywhere;
  }

  .premium-card__arrow {
    position: relative;
    right: auto;
    bottom: auto;
    font-size: 18px;
  }

  .support-card--top {
    grid-column: 1 / -1;
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-rows: 1fr;
    align-items: center;
    min-height: 88px;
    padding: 12px 14px;
    gap: 10px;
    background-position: center 36%;
  }

  .support-card--top .feature-icon {
    width: 38px;
    height: 38px;
  }

  .support-card--top::before {
    top: auto;
    right: 14px;
    bottom: 0;
    left: 14px;
    width: auto;
    height: 3px;
    border-radius: 999px 999px 0 0;
    box-shadow: 0 -5px 20px rgba(224, 177, 60, 0.42);
  }

  .support-card--top .feature-copy h3 {
    max-width: none;
    font-size: var(--ff-type-body-small);
  }

  .support-card--top .support-top-label {
    margin-bottom: 3px;
    font-size: 0.6875rem;
  }

  .support-top-badge {
    min-width: 42px;
    min-height: 25px;
    padding: 5px 8px;
    font-size: 0.6875rem;
  }

  .feature-card,
  .feature-grid .feature-card--training {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    align-content: start;
    min-height: 152px;
    padding: 15px;
    gap: 12px;
    border-radius: 16px;
  }

  .feature-card .feature-icon,
  .feature-grid .feature-card--training :deep(.icon-column) {
    width: 44px;
    height: 44px;
  }

  .feature-card .feature-copy h3,
  .feature-grid .feature-card--training :deep(.header-text) {
    max-width: 12ch;
    margin: 0;
    font-size: var(--ff-type-body);
    line-height: 1.16;
  }

  .feature-card:not(.feature-card--featured) .feature-copy p,
  .feature-grid .feature-card--training :deep(.text-card) {
    display: none;
  }

  .feature-grid .feature-card--training.user-training-entry--disabled :deep(.text-card) {
    display: block;
    margin-top: 6px;
    font-size: var(--ff-type-label);
    line-height: 1.3;
  }

  .feature-card > .feature-arrow,
  .feature-grid .feature-card--training :deep(.icon-column-right) {
    right: 14px;
    bottom: 13px;
  }

  .feature-card--featured {
    grid-column: 1 / -1;
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-rows: 1fr;
    align-items: center;
    min-height: 124px;
    padding: 17px;
  }

  .feature-card--featured .feature-copy h3 {
    max-width: none;
    margin-bottom: 7px;
    font-size: 1rem;
  }

  .feature-card--featured .feature-copy p {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-size: var(--ff-type-body-small);
    line-height: 1.4;
  }

  .feature-card--featured > .feature-arrow {
    right: 18px;
    bottom: 16px;
  }

  .feature-card--wide {
    grid-column: 1 / -1;
    grid-template-columns: 44px minmax(0, 1fr) 28px;
    grid-template-rows: 1fr;
    align-items: center;
    min-height: 94px;
    padding: 15px 18px;
    gap: 14px;
    border-color: rgba(255, 255, 255, 0.22);
    background-color: rgba(17, 17, 16, 0.7);
    background-image:
      linear-gradient(145deg, rgba(28, 28, 27, 0.82), rgba(17, 17, 16, 0.64)),
      var(--card-image, none);
    background-position: center 45%;
    background-size: cover;
    background-repeat: no-repeat;
    color: var(--ff-white);
    box-shadow:
      0 18px 44px rgba(17, 17, 16, 0.17),
      inset 0 1px 0 rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(26px) saturate(1.35);
    -webkit-backdrop-filter: blur(26px) saturate(1.35);
  }

  .feature-card--wide:hover {
    border-color: rgba(255, 255, 255, 0.34);
    background-color: rgba(17, 17, 16, 0.74);
    background-image:
      linear-gradient(145deg, rgba(34, 34, 32, 0.84), rgba(20, 20, 19, 0.68)),
      var(--card-image, none);
    color: var(--ff-white);
  }

  .feature-card--wide .feature-icon {
    align-self: center;
    margin: 0;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.08);
    color: var(--ff-white);
  }

  .feature-card--wide .feature-copy {
    align-self: center;
    min-width: 0;
    padding: 0;
  }

  .feature-card--wide .feature-copy h3 {
    max-width: none;
    margin: 0;
    color: var(--ff-white);
    font-size: min(var(--ff-type-body), 6vw);
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  .feature-card--wide > .feature-arrow {
    position: static;
    grid-column: 3;
    grid-row: 1;
    align-self: center;
    justify-self: end;
    top: auto;
    right: auto;
    bottom: auto;
    margin: 0;
    color: var(--ff-white);
    font-size: 22px;
    transform: none;
  }

  .feature-badge {
    top: 13px;
    right: 13px;
    padding: 5px 7px;
    font-size: 0.6875rem;
  }

  .feature-grid .feature-card--training :deep(.text-column) {
    align-self: start;
  }
}

@media (max-width: 390px) {
  .account-button {
    padding-inline: 9px;
    font-size: min(var(--ff-type-label), 6vw);
  }

  .support-card,
  .feature-card,
  .feature-grid .feature-card--training {
    min-height: 144px;
    padding: 14px;
  }

  .support-card--top {
    min-height: 76px;
    padding: 11px 13px;
  }

  .premium-card {
    min-height: 84px;
    padding: 11px 12px;
  }

  .feature-card--featured {
    min-height: 118px;
    padding: 16px;
  }

  .feature-card--wide {
    min-height: 88px;
  }
}

@media (max-width: 700px) {
  .services-shop-card {
    min-height: 232px;
    padding: 21px 19px;
    border-radius: 20px;
  }

  .services-shop-card::before {
    background:
      linear-gradient(90deg, rgba(10, 10, 9, 0.98) 0%, rgba(10, 10, 9, 0.93) 55%, rgba(10, 10, 9, 0.75) 100%),
      linear-gradient(0deg, rgba(10, 10, 9, 0.48), transparent 64%);
  }

  .services-shop-card__visual img {
    object-position: 61% center;
  }

  .services-shop-card__copy {
    max-width: 100%;
  }

  .services-shop-card__copy strong {
    font-size: min(clamp(1.35rem, 7vw, 1.75rem), 10vw);
    overflow-wrap: anywhere;
  }

  .services-shop-card__copy small {
    font-size: min(0.68rem, 4vw);
    line-height: 1.34;
  }

  .services-shop-card__action {
    min-height: 38px;
    max-width: 100%;
    margin-top: 13px;
    padding: 8px 12px;
    font-size: min(0.6875rem, 5vw);
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .feature-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .feature-card--operational,
  .feature-grid .feature-card--training.feature-card--operational {
    grid-template-columns: 44px minmax(0, 1fr) 34px;
    grid-template-rows: 1fr;
    align-items: center;
    min-height: 86px;
    padding: 14px 14px 14px 16px;
    gap: 12px;
    border-radius: 18px;
  }

  .feature-card--operational::after {
    top: 16px;
    bottom: 16px;
    left: 0;
  }

  .feature-card--operational .feature-icon,
  .feature-grid .feature-card--training.feature-card--operational :deep(.icon-column) {
    width: 44px;
    height: 44px;
    align-self: center;
    border-radius: 14px;
  }

  .feature-card--operational .feature-copy,
  .feature-grid .feature-card--training.feature-card--operational :deep(.text-column) {
    align-self: center;
  }

  .feature-card--operational .feature-copy h3,
  .feature-grid .feature-card--training.feature-card--operational :deep(.header-text) {
    max-width: none;
    min-height: 0;
    font-size: min(clamp(0.9rem, 4.1vw, 1rem), 6vw);
    line-height: 1.15;
    overflow-wrap: anywhere;
  }

  .feature-card--operational > .feature-arrow,
  .feature-grid .feature-card--training.feature-card--operational :deep(.icon-column-right) {
    position: static;
    grid-column: 3;
    grid-row: 1;
    align-self: center;
    justify-self: end;
    width: 32px;
    height: 32px;
    margin: 0;
    border-radius: 50%;
  }

  .feature-grid .feature-card--training.feature-card--operational.user-training-entry--disabled {
    grid-template-columns: 44px minmax(0, 1fr);
  }

  .feature-grid .feature-card--training.feature-card--operational.user-training-entry--disabled :deep(.text-card) {
    display: -webkit-box;
    margin-top: 5px;
    overflow: hidden;
    font-size: 0.625rem;
    line-height: 1.25;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
}

@media (prefers-reduced-motion: reduce) {
  .support-card,
  .feature-card {
    animation: none;
    transition: none;
  }
}
</style>
